/**
 * Storefront preview mode — the store as framed by the backoffice
 * "Preview & edit" page (docs/storefront-preview-and-edit.md).
 *
 * `?preview=1` turns it on. Inside the preview frame it is persisted
 * (sessionStorage), because the checkout rebuilds query strings between steps;
 * in a normal tab it is not (see isStorefrontPreview).
 *
 * It is a presentation flag, not a permission: anyone can add it to a store URL,
 * and all it does is turn off the host's analytics, tag the cart as a preview
 * (so it stays out of conversion analytics), and refuse to submit a booking.
 */
export const STOREFRONT_PREVIEW_PARAM = 'preview';

const STORAGE_KEY = 'fw-store-preview';

export const STOREFRONT_PREVIEW_BLOCKED_MESSAGE = 'This is a preview of your store. Bookings are turned off here.';

function readParam(search: string): boolean | null {
    const params = new URLSearchParams(search);

    if (!params.has(STOREFRONT_PREVIEW_PARAM)) {
        return null;
    }

    const raw = (params.get(STOREFRONT_PREVIEW_PARAM) ?? '').toLowerCase();

    if (['0', 'false', 'no'].includes(raw)) {
        return false;
    }

    // `?preview` with no value counts as on.
    return true;
}

/**
 * Whether this page is framed — the backoffice preview always is. A cross-origin
 * parent throws on access, which also means framed.
 */
export function isFramed(win: Window | undefined = typeof window !== 'undefined' ? window : undefined): boolean {
    if (!win) {
        return false;
    }

    try {
        return win.self !== win.top;
    } catch {
        return true;
    }
}

/**
 * Preview mode is only remembered inside the preview frame. In a normal tab it
 * applies to a URL that says `?preview=1` and nothing else, so a tab that once
 * opened a preview link doesn't keep the ribbon, analytics-off and blocked
 * bookings on every later store page (and any leftover flag is cleared).
 */
export function isStorefrontPreview(
    search: string = typeof window !== 'undefined' ? window.location.search : '',
    framed: boolean = isFramed(),
): boolean {
    const requested = readParam(search);

    try {
        if (!framed) {
            sessionStorage.removeItem(STORAGE_KEY);
            return requested === true;
        }

        if (requested === true) {
            sessionStorage.setItem(STORAGE_KEY, '1');
            return true;
        }

        if (requested === false) {
            sessionStorage.removeItem(STORAGE_KEY);
            return false;
        }

        return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        // sessionStorage unavailable (privacy mode) — the query flag still applies per page.
        return requested === true;
    }
}

/**
 * Whether a cart saved in this browser can be reused. A cart from the other
 * mode cannot: a host's earlier real visit must not become the preview's cart,
 * and a leftover preview cart would refuse a real booking later.
 */
export function isCartReusable(cart: { is_preview?: boolean | null } | null | undefined, preview: boolean = isStorefrontPreview()): boolean {
    return Boolean(cart?.is_preview) === preview;
}
